import { useState, useEffect, useRef, useCallback } from "react";

export default function Quiz() {
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [serverResponse, setServerResponse] = useState(null);
  const timerRef = useRef(null);
  const apiUrl = process.env.REACT_APP_CAPITALYA_API_URL;

  const today = new Date().getDay();

  const isQuizDay = today === 1 || today === 5;

  // Récupérer l'historique
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const response = await fetch(
          `${apiUrl}/api/tasks/quiz-history`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.status === "success") {
            setHistory(data.history || []);
          }
        }
      } catch (err) {
        console.error("Erreur fetch history:", err);
      }
    };

    fetchHistory();
  }, [quizCompleted, apiUrl]);

  // Récupérer les questions (2 max par jour)
  useEffect(() => {
    const fetchQuiz = async () => {
      if (!isQuizDay) {
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem("token");

        if (!token) {
          setError("Vous devez être connecté");
          setLoading(false);
          return;
        }

        const res = await fetch(`${apiUrl}/api/tasks/quiz-list`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        const data = await res.json();

        if (data.status === "success" && data.questions?.length > 0) {
          setQuizQuestions(data.questions);
        } else {
          setError(data.message || "Aucune question disponible");
        }

        setLoading(false);
      } catch (err) {
        setError("Erreur de connexion au serveur");
        setLoading(false);
      }
    };

    fetchQuiz();
  }, [isQuizDay, apiUrl]);

  const handleSubmitAnswer = useCallback(
    (timeUp = false) => {
      // Timeout sans réponse
      if (timeUp && selectedAnswer === null) {
        setIsCorrect(false);
        setShowResult(true);
        setAnsweredQuestions((prev) => [
          ...prev,
          {
            questionId: quizQuestions[currentQuestion].id,
            selectedAnswer: null, // Aucune réponse sélectionnée
          },
        ]);
        if (timerRef.current) clearInterval(timerRef.current);
        return;
      }

      if (selectedAnswer === null && !timeUp) return;

      const correct =
        selectedAnswer === quizQuestions[currentQuestion].correctAnswer;
      setIsCorrect(correct);
      setShowResult(true);

      // Stocker la réponse avec les données nécessaires pour le back
      setAnsweredQuestions((prev) => [
        ...prev,
        {
          questionId: quizQuestions[currentQuestion].id,
          selectedAnswer: selectedAnswer,
        },
      ]);

      if (timerRef.current) clearInterval(timerRef.current);
    },
    [selectedAnswer, currentQuestion, quizQuestions]
  );

  // Timer (30 secondes par question)
  useEffect(() => {
    if (!quizQuestions.length || showResult || quizCompleted) return;

    setTimeLeft(30);
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleSubmitAnswer(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [currentQuestion, showResult, quizQuestions.length, quizCompleted, handleSubmitAnswer]);

  const handleAnswerSelect = (index) => {
    if (!showResult) {
      setSelectedAnswer(index);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowResult(false);
      setIsCorrect(false);
    } else {
      setQuizCompleted(true);
      saveQuizScore();
    }
  };

  const saveQuizScore = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${apiUrl}/api/tasks/quiz-submit`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          answers: answeredQuestions
        }),
      });

      const data = await res.json();

      if (data.status === "success") {
        setServerResponse(data);
      }

      // console.log("Score sauvegardé:", data);
    } catch (err) {
      console.error("Erreur saveQuizScore:", err);
    }
  };

  // Calcul des réponses correctes pour l'affichage
  const correctAnswersCount = answeredQuestions.filter((answer, index) => {
    if (!quizQuestions[index]) return false;
    return answer.selectedAnswer === quizQuestions[index].correctAnswer;
  }).length;

  // Composant Historique réutilisable
  const HistorySection = () => {
    if (history.length === 0) return null;

    return (
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">
          📊 Historique
        </h3>
        <div className="space-y-2">
          {history.map((entry, index) => (
            <div
              key={index}
              className="flex justify-between items-center py-2 border-b border-gray-100"
            >
              <div>
                <p className="text-sm font-medium text-gray-800">
                  {new Date(entry.doneAt).toLocaleDateString()}
                </p>
                <p className="text-xs font-medium text-gray-600">
                  {new Date(entry.doneAt).toLocaleTimeString()}
                </p>
                <p className="text-xs text-gray-500">
                  {entry.correctAnswers}/{entry.totalQuestions} correctes - {entry.percentage}%
                </p>
              </div>
              <span className={`font-bold ${entry.gain > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                {entry.gain || 0}F
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Loading
  if (loading) {
    return (
      <div className="space-y-4 pb-20">
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du quiz...</p>
        </div>
      </div>
    );
  }

  // Jour non autorisé
  if (!isQuizDay) {
    return (
      <div className="space-y-4 pb-20">
        <div className="bg-gradient-to-r from-orange-500 to-red-600 rounded-lg shadow-md p-6 text-white text-center">
          <div className="text-5xl mb-4">📅</div>
          <h2 className="text-2xl font-bold mb-2">Quiz non disponible</h2>
          <p className="text-lg mb-4">
            Le quiz est disponible uniquement le <strong>lundi</strong> et{" "}
            <strong>vendredi</strong>.
          </p>
          <div className="bg-white/20 rounded-lg p-3 text-sm">
            <p>Revenez ces jours-là !</p>
          </div>
        </div>

        <HistorySection />
      </div>
    );
  }

  // Erreur ou déjà joué
  if (error || quizQuestions.length === 0) {
    return (
      <div className="space-y-4 pb-20">
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold mb-2 text-gray-800">
            {error || "Aucune question disponible"}
          </h2>
          <p className="text-gray-600 mb-4">
            Revenez lundi ou vendredi prochain pour de nouvelles questions !
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            🔄 Rafraîchir
          </button>
        </div>

        <HistorySection />
      </div>
    );
  }

  // Quiz terminé
  if (quizCompleted) {
    const gain = serverResponse?.message?.match(/\d+/)?.[0] || 0;
    const percentage = Math.round((correctAnswersCount / quizQuestions.length) * 100);

    return (
      <div className="space-y-4 pb-20">
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg shadow-md p-6 text-white text-center">
          <div className="text-5xl mb-4">
            {percentage >= 80 ? "🏆" : percentage >= 50 ? "🎉" : "💪"}
          </div>
          <h2 className="text-2xl md:text-3xl font-bold mb-2">
            Quiz Terminé !
          </h2>
          <p className="text-lg mb-4">Voici vos résultats</p>

          <div className="bg-white/20 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm opacity-90">Gain</p>
                <p className="text-3xl font-bold">{gain}F</p>
              </div>
              <div>
                <p className="text-sm opacity-90">Réussite</p>
                <p className="text-3xl font-bold">{percentage}%</p>
              </div>
            </div>
          </div>

          <div className="text-center mb-4">
            <p className="text-lg">
              {correctAnswersCount} / {quizQuestions.length} bonnes réponses
            </p>
          </div>

          {gain > 0 ? (
            <div className="bg-yellow-400 text-yellow-900 rounded-lg p-3">
              <p className="font-bold">🎁 Félicitations !</p>
              <p className="text-sm">{serverResponse?.message || `${gain}F ajoutés à votre solde`}</p>
            </div>
          ) : (
            <div className="bg-white/20 rounded-lg p-3">
              <p className="text-sm">
                Il vous fallait au moins 50% pour gagner des F CFA
              </p>
            </div>
          )}
        </div>

        <HistorySection />
      </div>
    );
  }

  const question = quizQuestions[currentQuestion];

  // Quiz en cours
  return (
    <div className="space-y-4 pb-20">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2 text-center">
          🧠 Quiz CAPITALYA
        </h2>
        <p className="text-sm text-gray-600 mb-4 text-center">
          {quizQuestions.length} question{quizQuestions.length > 1 ? "s" : ""} -
          30 secondes chacune
        </p>

        {/* Progress */}
        <div className="bg-gray-200 rounded-full h-2 mb-2">
          <div
            className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300"
            style={{
              width: `${((currentQuestion + 1) / quizQuestions.length) * 100}%`,
            }}
          ></div>
        </div>

        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>
            Question {currentQuestion + 1} / {quizQuestions.length}
          </span>
          <span
            className={`font-bold ${timeLeft <= 10 ? "text-red-600 animate-pulse" : "text-blue-600"
              }`}
          >
            ⏱ {timeLeft}s
          </span>
        </div>
      </div>

      {/* Question */}
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
        <div className="flex items-start gap-3 mb-6">
          <div className="bg-blue-100 text-blue-600 font-bold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
            {currentQuestion + 1}
          </div>
          <div className="flex-1">
            <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-2">
              {question.question}
            </h3>
            <p className="text-sm text-gray-600">
              💰 Récompense possible : {question.reward}F
            </p>
          </div>
        </div>

        {/* Options */}
        <div className="space-y-3 mb-6">
          {question.options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleAnswerSelect(index)}
              disabled={showResult}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${selectedAnswer === index
                ? showResult
                  ? isCorrect
                    ? "border-green-500 bg-green-50"
                    : "border-red-500 bg-red-50"
                  : "border-blue-500 bg-blue-50"
                : showResult && index === question.correctAnswer
                  ? "border-green-500 bg-green-50"
                  : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                } ${showResult ? "cursor-default" : "cursor-pointer"}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-800">{option}</span>
                {showResult && (
                  <>
                    {index === question.correctAnswer && (
                      <span className="text-xl">✅</span>
                    )}
                    {selectedAnswer === index && !isCorrect && (
                      <span className="text-xl">❌</span>
                    )}
                  </>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Feedback */}
        {showResult && (
          <div
            className={`rounded-lg p-4 mb-4 ${isCorrect
              ? "bg-green-50 border-l-4 border-green-500"
              : "bg-red-50 border-l-4 border-red-500"
              }`}
          >
            <div className="flex items-start gap-3">
              <div className="text-2xl">{isCorrect ? "🎉" : "😔"}</div>
              <div>
                <h4
                  className={`font-bold mb-1 ${isCorrect ? "text-green-800" : "text-red-800"
                    }`}
                >
                  {isCorrect ? "Bonne réponse !" : "Mauvaise réponse"}
                </h4>
                <p
                  className={`text-sm ${isCorrect ? "text-green-700" : "text-red-700"
                    }`}
                >
                  {isCorrect
                    ? `Vous pourriez gagner ${question.reward}F !`
                    : `Bonne réponse : ${question.options[question.correctAnswer]
                    }`}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Buttons */}
        {!showResult ? (
          <button
            onClick={() => handleSubmitAnswer(false)}
            disabled={selectedAnswer === null}
            className={`w-full font-bold py-3 md:py-4 px-6 rounded-lg transition-all duration-300 ${selectedAnswer === null
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white transform hover:scale-105 shadow-lg"
              }`}
          >
            {selectedAnswer === null
              ? "Sélectionnez une réponse"
              : "✅ Valider"}
          </button>
        ) : (
          <button
            onClick={handleNextQuestion}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-3 md:py-4 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            {currentQuestion < quizQuestions.length - 1
              ? "➡️ Question Suivante"
              : "🏁 Voir les Résultats"}
          </button>
        )}
      </div>

      {/* Info */}
      <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
        <h4 className="font-bold text-blue-800 mb-2">ℹ️ Guide</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• 2 questions par jour (lundi et vendredi uniquement)</li>
          <li>• 30 secondes par question</li>
          <li>• 50F pour 50% de réussite, 100F pour 100%</li>
        </ul>
      </div>
    </div>
  );
}